import { useEffect } from 'react';
import { Box, Container, Heading, ListItem, OrderedList, Stack, Text, UnorderedList } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LandingLayout } from './LandingLayout';
import { LandingHeader } from './components/LandingHeader';
import { LandingFooter } from './components/LandingFooter';
import { Seo } from './seo/Seo';
import { TERMS, PRIVACY, type LegalDoc, type LegalLocale } from './content/legal';
import { useContent } from '../content/useContent';
import { RenderedHtml } from '../content/RenderedHtml';

type LegalKind = 'terms' | 'privacy';

const DOCS: Record<
  LegalKind,
  { docs: Record<LegalLocale, LegalDoc>; path: string; description: Record<LegalLocale, string> }
> = {
  terms: {
    docs: TERMS,
    path: '/terms',
    description: {
      en: 'The terms governing use of Streamko, the subscription-based business music streaming and audio management service operated by Eva Media LLC.',
      uk: 'Умови використання Streamko — сервісу трансляції фонової музики та керування аудіо для бізнесу за передплатою (Eva Media LLC).',
      de: 'Die Nutzungsbedingungen für Streamko — den abonnementbasierten Business-Musikstreaming- und Audio-Management-Dienst der Eva Media LLC.',
    },
  },
  privacy: {
    docs: PRIVACY,
    path: '/privacy',
    description: {
      en: 'How Streamko, operated by Eva Media LLC, collects, uses, and protects personal information when you use the service.',
      uk: 'Як Streamko (Eva Media LLC) збирає, використовує та захищає персональну інформацію під час користування сервісом.',
      de: 'Wie Streamko (Eva Media LLC) personenbezogene Daten erhebt, verwendet und schützt, wenn Sie den Dienst nutzen.',
    },
  },
};

const LAST_UPDATED_LABEL: Record<LegalLocale, string> = {
  en: 'Last updated:',
  uk: 'Останнє оновлення:',
  de: 'Zuletzt aktualisiert:',
};

/** Clears the sticky marketing header when jumping to #section anchors. */
const HEADING_SCROLL_MARGIN = { base: '84px', xl: '104px' };

/** Structured render from the bundled `legal.ts` — used for prerender/SEO and as the
 *  offline fallback when the CMS API hasn't (yet) returned. */
function StructuredDoc({ doc }: { doc: LegalDoc }) {
  return (
    <Stack spacing={{ base: 8, lg: '40px' }} maxW="886px" mx="auto">
      {doc.sections.map((section) => (
        <Stack key={section.id} spacing="12px">
          <Heading
            as="h2"
            id={section.id}
            scrollMarginTop={HEADING_SCROLL_MARGIN}
            fontSize="xl"
            fontWeight={600}
            lineHeight={1.3}
            color="onboarding.deepPlum"
          >
            {section.heading}
          </Heading>
          {section.paragraphs.map((p, i) => (
            <Text key={i} fontSize="md" lineHeight={1.7} color="onboarding.deepPlum">
              {p}
            </Text>
          ))}
          {section.bullets && (
            <UnorderedList spacing="6px" pl="20px" color="onboarding.deepPlum">
              {section.bullets.map((b, i) => (
                <ListItem key={i} fontSize="md" lineHeight={1.7}>
                  {b}
                </ListItem>
              ))}
            </UnorderedList>
          )}
          {section.steps && (
            <OrderedList spacing="6px" pl="20px" color="onboarding.deepPlum">
              {section.steps.map((s, i) => (
                <ListItem key={i} fontSize="md" lineHeight={1.7}>
                  {s}
                </ListItem>
              ))}
            </OrderedList>
          )}
          {section.paragraphsAfter?.map((p, i) => (
            <Text key={i} fontSize="md" lineHeight={1.7} color="onboarding.deepPlum">
              {p}
            </Text>
          ))}
          {section.contact && (
            <Stack spacing="2px" pt="6px">
              <Text fontSize="md" fontWeight={600} color="onboarding.deepPlum">
                {section.contact.name}
              </Text>
              {section.contact.lines.map((line, i) => (
                <Text key={i} fontSize="md" color="onboarding.deepPlum">
                  {line}
                </Text>
              ))}
            </Stack>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * Standalone, unauthenticated legal page (Terms of Service / Privacy Policy).
 * CMS-driven: renders the managed content (GET /api/v1/content/{terms|privacy}) when
 * available, falling back to the bundled `content/legal` copy so prerender/SEO and
 * offline both keep working. Linked from the footer and external surfaces.
 */
export function LegalPage({ kind }: { kind: LegalKind }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const locale: LegalLocale = lang === 'uk' || lang === 'de' ? lang : 'en';
  const { docs, path, description } = DOCS[kind];
  const doc = docs[locale];
  const { data } = useContent(kind);
  const title = data?.title || doc.title;

  // Deep links like /terms#account-deletion (used by external reviewers, e.g.
  // Facebook's account-deletion URL requirement). The SPA doesn't scroll to the
  // fragment natively, and the CMS body arrives async — so re-run once the
  // rendered content (fallback → CMS) settles.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    const frame = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView();
    });
    return () => cancelAnimationFrame(frame);
  }, [hash, data]);

  return (
    <LandingLayout header={<LandingHeader />} footer={<LandingFooter />}>
      <Seo title={`${title} — Streamko`} description={description[locale]} path={path} locale={locale} />
      {/* Title block — full-bleed warm gradient (Figma 2:951: #FFFAF9 → #FBE5D8, ~140°). */}
      <Box bgGradient="linear(140deg, #FFFAF9 22%, #FBE5D8 83%)" color="onboarding.deepPlum">
        <Container maxW="1440px" px={{ base: 5, md: 8 }} py={{ base: '56px', lg: '104px' }}>
          <Stack spacing="10px" textAlign="center" align="center">
            <Heading
              as="h1"
              fontFamily="heading"
              fontWeight={700}
              fontSize="display.lg"
              lineHeight={1.1}
              letterSpacing="-0.01em"
              color="onboarding.deepPlum"
            >
              {title}
            </Heading>
            {!data && (
              <Text fontSize="md" color="onboarding.mutedRose">
                {LAST_UPDATED_LABEL[locale]} {doc.updated}
              </Text>
            )}
          </Stack>
        </Container>
      </Box>

      {/* Content — CMS HTML when available, structured fallback otherwise */}
      <Box as="section" bg="white" color="onboarding.deepPlum">
        <Container maxW="1440px" px={{ base: 5, md: 8 }} py={{ base: 12, lg: 20 }}>
          {data?.bodyHtml ? (
            <Box maxW="886px" mx="auto">
              <RenderedHtml html={data.bodyHtml} />
            </Box>
          ) : (
            <StructuredDoc doc={doc} />
          )}
        </Container>
      </Box>
    </LandingLayout>
  );
}

export default LegalPage;
